"use client"

import { useState, useEffect, useRef } from "react"
import {
  Card, CardContent, CardMedia, Typography, Box,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Button, TextField, CircularProgress,
  Select, MenuItem, FormControl, InputLabel, FormHelperText, InputAdornment,
} from "@mui/material"
import Close from "@mui/icons-material/Close"
import EditIcon from "@mui/icons-material/Edit"
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera"
import { FaSwimmingPool, FaUmbrellaBeach, FaBell, FaCoffee, FaShower } from "react-icons/fa"
import {
  collection, getDocs, doc, setDoc, updateDoc, addDoc, deleteDoc
} from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL } from "firebase/storage"
import { db, storage } from "../config/firebase"
import { useAuth } from "../AuthContext"

import roomImg1 from "../assets/images/villa-images/image1.webp"
import roomImg2 from "../assets/images/villa-images/image2.webp"
import roomImg3 from "../assets/images/villa-images/image3.webp"
import roomImg4 from "../assets/images/villa-images/image4.webp"
import roomImg5 from "../assets/images/villa-images/image5.webp"
import roomImg6 from "../assets/images/villa-images/image6.webp"
import roomImg7 from "../assets/images/villa-images/image7.webp"
import roomImg8 from "../assets/images/villa-images/image8.webp"
import Footer from "./Footer"
import PageHeader from "./PageHeader"

const IMAGE_MAP = {
  roomImg1, roomImg2, roomImg3, roomImg4,
  roomImg5, roomImg6, roomImg7, roomImg8,
}

const AMENITY_ICONS = [
  <FaSwimmingPool />, <FaUmbrellaBeach />,
  <FaBell />, <FaCoffee />, <FaShower />,
]

const MAX_OCCUPANCY_OPTIONS = Array.from({ length: 10 }, (_, i) => `${i + 1} guest${i === 0 ? "" : "s"}`)

const PROPERTY_TYPE_OPTIONS = ["Suite", "Villa"]

const BED_CONFIG_OPTIONS = [
  "1 King",
  "1 Queen",
  "2 Queens",
  "2 Kings",
  "1 King, 1 Twin",
  "1 King, 2 Twins",
  "1 King, 1 Sofa Bed",
  "2 Kings, 1 Sofa Bed",
  "2 Kings, 1 Queen",
  "1 Queen, 1 Sofa Bed",
  "1 Queen, 2 Twins",
]

const DEFAULT_VILLAS = [
  { id: "villa-1",  name: "Sunset Palms Suite",      subtitle: "Premium Beachfront",   imageKey: "roomImg1", price: "$349", size: "1,200 sq ft", maxOccupancy: "4 guests", bedConfig: "1 King, 1 Sofa Bed",   amenitiesLabel: "Suite Amenities", amenities: [{ text: "Private infinity pool" }, { text: "Direct beach access" }, { text: "Dedicated concierge" }, { text: "Breakfast included" }, { text: "Outdoor rainfall shower" }] },
  { id: "villa-2",  name: "Azure Coast Suite",        subtitle: "Luxury Oceanfront",    imageKey: "roomImg2", price: "$429", size: "1,400 sq ft", maxOccupancy: "5 guests", bedConfig: "2 Kings, 1 Sofa Bed",  amenitiesLabel: "Suite Amenities", amenities: [{ text: "Private infinity pool" }, { text: "Direct beach access" }, { text: "Butler service" }, { text: "Premium breakfast" }, { text: "Spa-style bathroom" }] },
  { id: "villa-3",  name: "Coral Reef Suite",         subtitle: "Oceanview Retreat",    imageKey: "roomImg3", price: "$389", size: "1,050 sq ft", maxOccupancy: "3 guests", bedConfig: "1 King, 1 Sofa Bed",   amenitiesLabel: "Suite Amenities", amenities: [{ text: "Shared infinity pool access" }, { text: "Panoramic ocean views" }, { text: "Evening turndown service" }, { text: "In-suite Nespresso bar" }, { text: "Rainfall shower & soaking tub" }] },
  { id: "villa-4",  name: "Luna Mar Suite",           subtitle: "Skyline Penthouse",    imageKey: "roomImg4", price: "$469", size: "1,600 sq ft", maxOccupancy: "4 guests", bedConfig: "1 King, 2 Twins",      amenitiesLabel: "Suite Amenities", amenities: [{ text: "Rooftop plunge pool" }, { text: "Sunset terrace lounge" }, { text: "Private host & concierge" }, { text: "Gourmet coffee & tea bar" }, { text: "Spa-inspired bath suite" }] },
  { id: "villa-5",  name: "Caribbean Breeze Villa",   subtitle: "Garden Retreat",       imageKey: "roomImg2", price: "$289", size: "950 sq ft",   maxOccupancy: "2 guests", bedConfig: "1 King",               amenitiesLabel: "Villa Amenities", amenities: [{ text: "Private plunge pool" }, { text: "Partial ocean view" }, { text: "Dedicated concierge" }, { text: "Breakfast included" }, { text: "Outdoor shower" }] },
  { id: "villa-6",  name: "Ocean Breeze Villa",       subtitle: "Hillside Escape",      imageKey: "roomImg3", price: "$319", size: "1,050 sq ft", maxOccupancy: "3 guests", bedConfig: "1 Queen, 1 Sofa Bed",  amenitiesLabel: "Villa Amenities", amenities: [{ text: "Shared infinity pool" }, { text: "Shuttle to beach" }, { text: "On-call concierge" }, { text: "Complimentary breakfast" }, { text: "Rainfall shower" }] },
  { id: "villa-7",  name: "Blue Horizon Villa",       subtitle: "Modern Oceanfront",    imageKey: "roomImg4", price: "$379", size: "1,300 sq ft", maxOccupancy: "4 guests", bedConfig: "2 Queens",             amenitiesLabel: "Villa Amenities", amenities: [{ text: "Private rooftop pool" }, { text: "Oceanfront view" }, { text: "Private host" }, { text: "Espresso machine" }, { text: "Spa shower" }] },
  { id: "villa-8",  name: "Tropical Hideaway Villa",  subtitle: "Jungle View",          imageKey: "roomImg5", price: "$249", size: "800 sq ft",   maxOccupancy: "2 guests", bedConfig: "1 King",               amenitiesLabel: "Villa Amenities", amenities: [{ text: "Jungle plunge pool" }, { text: "Beach shuttle access" }, { text: "On-demand service" }, { text: "Morning coffee delivery" }, { text: "Open-air shower" }] },
  { id: "villa-9",  name: "Vista Del Mar Villa",      subtitle: "Panoramic View",       imageKey: "roomImg6", price: "$399", size: "1,500 sq ft", maxOccupancy: "2 guests", bedConfig: "1 King",               amenitiesLabel: "Villa Amenities", amenities: [{ text: "Private infinity edge pool" }, { text: "Private beach cabana" }, { text: "Butler service" }, { text: "In-villa breakfast" }, { text: "Luxury bath & shower" }] },
  { id: "villa-10", name: "Serenity Sands Villa",     subtitle: "Beachside Comfort",    imageKey: "roomImg7", price: "$279", size: "1,000 sq ft", maxOccupancy: "4 guests", bedConfig: "1 Queen, 2 Twins",     amenitiesLabel: "Villa Amenities", amenities: [{ text: "Family pool access" }, { text: "Steps from beach" }, { text: "Daily cleaning" }, { text: "Coffee & snack bar" }, { text: "Rainfall shower" }] },
  { id: "villa-11", name: "Palm Grove Villa",         subtitle: "Private Estate",       imageKey: "roomImg8", price: "$499", size: "2,000 sq ft", maxOccupancy: "6 guests", bedConfig: "2 Kings, 1 Queen",     amenitiesLabel: "Villa Amenities", amenities: [{ text: "Large private pool" }, { text: "Private beach path" }, { text: "24/7 concierge" }, { text: "Full kitchen & bar" }, { text: "Luxury walk-in showers" }] },
  { id: "villa-12", name: "Emerald Bay Villa",        subtitle: "Coastal Paradise",     imageKey: "roomImg1", price: "$329", size: "1,150 sq ft", maxOccupancy: "3 guests", bedConfig: "1 King, 1 Twin",       amenitiesLabel: "Villa Amenities", amenities: [{ text: "Private pool" }, { text: "Beach access" }, { text: "Concierge service" }, { text: "Breakfast included" }, { text: "Rain shower" }] },
]

const modernDisplayFont = "Satoshi, Inter, SF Pro Display, system-ui, sans-serif"
const modernBodyFont    = "Inter, SF Pro Text, system-ui, sans-serif"

// Resolve image — prefer Firestore URL, fall back to local import
const resolveImage = (villa) => {
  if (villa.imageUrl) return villa.imageUrl
  if (villa.imageKey && IMAGE_MAP[villa.imageKey]) return IMAGE_MAP[villa.imageKey]
  return roomImg1
}

// Strip everything but digits/decimal point, then re-add a single leading $ — handles legacy "$349" data too
const sanitizePrice = (raw) => {
  let value = String(raw || "").replace(/[^0-9.]/g, "")
  const firstDot = value.indexOf(".")
  if (firstDot !== -1) {
    value = value.slice(0, firstDot + 1) + value.slice(firstDot + 1).replace(/\./g, "")
  }
  const [whole, decimal] = value.split(".")
  return decimal !== undefined ? `${whole}.${decimal.slice(0, 2)}` : value
}

const formatPrice = (raw) => {
  const cleaned = sanitizePrice(raw)
  return cleaned ? `$${cleaned}` : "—"
}

export default function RoomCard() {
  const { isAdmin } = useAuth()
  const [villas, setVillas]       = useState([])
  const [loading, setLoading]     = useState(true)
  const [openVilla, setOpenVilla] = useState(null)

  // Edit state
  const [editVilla, setEditVilla]       = useState(null)
  const [editForm, setEditForm]         = useState({})
  const [saving, setSaving]             = useState(false)
  const [savedMsg, setSavedMsg]         = useState(false)
  const [uploadingImg, setUploadingImg] = useState(false)
  const [previewUrl, setPreviewUrl]     = useState(null)
  const fileInputRef = useRef(null)

  // ── Load / seed ──────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, "villas"))
        if (snap.empty) {
          const seeded = DEFAULT_VILLAS.map((v) => ({ ...v, available: true }))
          await Promise.all(seeded.map((v) => setDoc(doc(db, "villas", v.id), v)))
          setVillas(seeded)
        } else {
          const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }))
          data.sort((a, b) => a.id.localeCompare(b.id))
          setVillas(data)
        }
      } catch (err) {
        console.error(err)
        setVillas(DEFAULT_VILLAS)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  // ── View details ─────────────────────────────────────────────────────
  const handleOpen  = (v) => setOpenVilla(v)
  const handleClose = () => setOpenVilla(null)

  // ── Edit open / close ────────────────────────────────────────────────
  const handleEditOpen = (e, villa) => {
    if (e) e.stopPropagation()
    setEditVilla(villa)
    setPreviewUrl(resolveImage(villa))
    // Derive propertyType from explicit field, then fall back to amenitiesLabel for legacy data
    const derivedType = villa.propertyType
      || (villa.amenitiesLabel?.includes("Suite") ? "Suite" : "")
      || (villa.amenitiesLabel?.includes("Villa") ? "Villa" : "")
    setEditForm({
      name:          villa.name         || "",
      subtitle:      villa.subtitle     || "",
      price:         sanitizePrice(villa.price),
      size:          villa.size         || "",
      maxOccupancy:  villa.maxOccupancy || "",
      bedConfig:     villa.bedConfig    || "",
      amenitiesText: (villa.amenities || []).map((a) => a.text).join("\n"),
      propertyType:  derivedType,
    })
    setSavedMsg(false)
  }

  const handleEditClose = () => {
    setEditVilla(null)
    setEditForm({})
    setPreviewUrl(null)
    setSavedMsg(false)
  }

  // ── Image upload ─────────────────────────────────────────────────────
  const handleImageChange = async (e) => {
    const file = e.target.files[0]
    if (!file) return

    // Show local preview immediately
    setPreviewUrl(URL.createObjectURL(file))
    setUploadingImg(true)

    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `villa-images/${editVilla.id}-${Date.now()}`)
      await uploadBytes(storageRef, file)
      const downloadUrl = await getDownloadURL(storageRef)

      // Save URL to Firestore right away
      await updateDoc(doc(db, "villas", editVilla.id), { imageUrl: downloadUrl })

      // Update local state so card shows new image immediately
      setVillas((prev) =>
        prev.map((v) =>
          v.id === editVilla.id ? { ...v, imageUrl: downloadUrl } : v
        )
      )
      setEditVilla((prev) => ({ ...prev, imageUrl: downloadUrl }))
      setPreviewUrl(downloadUrl)
    } catch (err) {
      console.error("Image upload error:", err)
    } finally {
      setUploadingImg(false)
    }
  }

  // ── Add new / Delete ─────────────────────────────────────────────────
  const handleAddNew = async () => {
    const newVilla = {
      name: "New Suite/Villa",
      subtitle: "Add a short description",
      price: "0",
      size: "0 sq ft",
      maxOccupancy: "2 guests",
      bedConfig: "1 King",
      propertyType: "",
      amenitiesLabel: "Amenities",
      amenities: [],
      available: true,
    }
    try {
      const ref = await addDoc(collection(db, "villas"), newVilla)
      const created = { id: ref.id, ...newVilla }
      setVillas((prev) => [...prev, created])
      handleEditOpen(null, created)
    } catch (err) {
      console.error("Add villa error:", err)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm("Delete this listing? This cannot be undone.")) return
    setSaving(true)
    try {
      await deleteDoc(doc(db, "villas", editVilla.id))
      setVillas((prev) => prev.filter((v) => v.id !== editVilla.id))
      handleEditClose()
    } catch (err) {
      console.error("Delete villa error:", err)
    } finally {
      setSaving(false)
    }
  }

  // ── Availability toggle ───────────────────────────────────────────────
  const handleToggleAvailability = async (e, villa) => {
    e.stopPropagation()
    const newAvailable = villa.available === false
    try {
      await updateDoc(doc(db, "villas", villa.id), { available: newAvailable })
      setVillas((prev) =>
        prev.map((v) => v.id === villa.id ? { ...v, available: newAvailable } : v)
      )
      if (editVilla?.id === villa.id) {
        setEditVilla((prev) => ({ ...prev, available: newAvailable }))
      }
    } catch (err) {
      console.error("Availability toggle error:", err)
    }
  }

  // ── Save text fields ─────────────────────────────────────────────────
  const handleEditSave = async () => {
    setSaving(true)
    try {
      const updatedAmenities = editForm.amenitiesText
        .split("\n").map((t) => t.trim()).filter(Boolean).map((text) => ({ text }))

      const updated = {
        name:           editForm.name,
        subtitle:       editForm.subtitle,
        price:          editForm.price,
        size:           editForm.size,
        maxOccupancy:   editForm.maxOccupancy,
        bedConfig:      editForm.bedConfig,
        amenities:      updatedAmenities,
        propertyType:   editForm.propertyType || "",
        amenitiesLabel: editForm.propertyType ? `${editForm.propertyType} Amenities` : "Amenities",
      }

      await updateDoc(doc(db, "villas", editVilla.id), updated)
      setVillas((prev) =>
        prev.map((v) => v.id === editVilla.id ? { ...v, ...updated } : v)
      )
      setSavedMsg(true)
      setTimeout(handleEditClose, 1200)
    } catch (err) {
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh" }}>
        <Typography sx={{ color: "white", fontFamily: modernBodyFont }}>Loading...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ position: "relative", minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <video autoPlay loop muted playsInline
        style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: -1 }}>
        <source src="/videos/sunsetbluedark.mp4" type="video/mp4" />
      </video>

      <Box sx={{ width: "100%", minHeight: "100vh", display: "flex", flexDirection: "column", position: "relative", zIndex: 1, flex: 1 }}>
        <Box sx={{ padding: 4, flex: 1, paddingTop: 3 }}>

          {/* Header */}
          <PageHeader
            docId="villas-header"
            defaultTitle="Suites & Villas"
            defaultDescription="Discover a private sanctuary where every suite/villa is crafted for serenity, comfort, and unforgettable memories. Wake to ocean breezes, lounge by our pool(s), and gain an out of this world experience in paradise."
          />

          {/* Cards */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(3,1fr)", lg: "repeat(4,1fr)" }, gap: 4, mt: 2, maxWidth: "1400px", margin: "0 auto" }}>
            {villas.map((villa) => (
              <Box key={villa.id} sx={{ width: "100%", display: "flex", height: "100%", position: "relative" }}>

                {/* Pencil icon — admin only */}
                {isAdmin && (
                  <IconButton onClick={(e) => handleEditOpen(e, villa)} size="small"
                    sx={{ position: "absolute", top: 8, left: 8, zIndex: 10, backgroundColor: "rgba(255,255,255,0.92)", width: 30, height: 30, boxShadow: "0 2px 8px rgba(0,0,0,0.3)", "&:hover": { backgroundColor: "#fff", transform: "scale(1.1)" } }}>
                    <EditIcon sx={{ fontSize: 15, color: "#0d1b4c" }} />
                  </IconButton>
                )}

                {/* Availability badge — visible to all; admins can click to toggle */}
                <Box
                  onClick={isAdmin ? (e) => handleToggleAvailability(e, villa) : undefined}
                  sx={{ position: "absolute", top: 8, left: isAdmin ? 46 : 8, zIndex: 10, cursor: isAdmin ? "pointer" : "default", backgroundColor: villa.available !== false ? "rgba(22,163,74,0.9)" : "rgba(185,28,28,0.85)", color: "#fff", px: 1, py: 0.25, borderRadius: "12px", fontSize: "0.65rem", fontWeight: 700, lineHeight: "20px", height: 20, display: "flex", alignItems: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.3)", userSelect: "none", ...(isAdmin && { "&:hover": { filter: "brightness(1.1)" } }) }}>
                  {villa.available !== false ? "Available" : "Unavailable"}
                </Box>

                <Card sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%", minHeight: 360, opacity: villa.available !== false ? 1 : 0.7, backgroundColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(24px)", border: villa.available !== false ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(185,28,28,0.4)", borderRadius: "16px", boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)", transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)", "&:hover": { transform: "translateY(-12px) scale(1.02)", backgroundColor: "rgba(255,255,255,0.12)", boxShadow: "0 40px 80px -12px rgba(0,0,0,0.4)" } }}>
                  <Box sx={{ position: "relative" }}>
                    <CardMedia component="img" image={resolveImage(villa)} alt={villa.name}
                      sx={{ height: 140, width: "100%", objectFit: "cover" }} />
                    <Box sx={{ position: "absolute", top: 8, right: 8, backgroundColor: "rgba(15,23,42,0.9)", color: "#fff", px: 1.5, py: 0.5, borderRadius: "16px", fontWeight: "bold", fontSize: "0.9rem" }}>
                      {formatPrice(villa.price)}<span style={{ fontWeight: 300, color: "rgba(203,213,225,1)" }}>/night</span>
                    </Box>
                  </Box>

                  <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden", pb: 0 }}>
                    <Typography variant="h6" sx={{ minHeight: 56, fontFamily: modernDisplayFont, color: "white", fontWeight: 600, textShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                      {villa.name}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ minHeight: 40, color: "rgba(255,255,255,0.85)", fontWeight: 400 }}>
                      {villa.subtitle}
                    </Typography>
                    <Box sx={{ mt: 1, fontSize: "0.95rem", color: "rgba(255,255,255,0.75)", fontFamily: modernBodyFont }}>
                      {villa.size} • {villa.maxOccupancy} • {villa.bedConfig}
                    </Box>
                    <Box sx={{ mt: 2, maxHeight: 72, overflowY: "auto" }}>
                      <Typography variant="subtitle2" fontWeight="600" sx={{ fontFamily: modernDisplayFont, color: "rgba(255,255,255,0.95)" }}>
                        {villa.amenitiesLabel}
                      </Typography>
                      <ul style={{ paddingLeft: "1.2em", margin: 0 }}>
                        {(villa.amenities || []).map((amenity, index) => (
                          <li key={index} style={{ display: "flex", alignItems: "center", marginBottom: 4, color: "white" }}>
                            <span style={{ marginRight: 6, color: "rgb(147,197,253)" }}>{AMENITY_ICONS[index] || <FaBell />}</span>
                            <Typography variant="body2" sx={{ color: "inherit", fontSize: "0.85rem" }}>{amenity.text}</Typography>
                          </li>
                        ))}
                      </ul>
                    </Box>
                    <Box sx={{ mt: "auto", display: "flex", justifyContent: "space-between", pt: 2 }}>
                      <Button variant="outlined" size="small" onClick={() => handleOpen(villa)}
                        sx={{ border: "1px solid rgba(255,255,255,0.25)", color: "white", backgroundColor: "rgba(255,255,255,0.08)", borderRadius: "8px", "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" } }}>
                        View Details
                      </Button>
                      <Button variant="contained" size="small" component="a" href="/booking"
                        sx={{ backgroundColor: "rgba(15,23,42,0.9)", color: "white", borderRadius: "8px", "&:hover": { backgroundColor: "rgba(15,23,42,1)" } }}>
                        Book Now
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Add New button — admin only */}
          {isAdmin && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
              <Button
                variant="outlined"
                onClick={handleAddNew}
                sx={{ color: "white", borderColor: "rgba(255,255,255,0.4)", borderRadius: "10px", px: 3, py: 1.25, fontSize: "0.9rem", fontWeight: 600, letterSpacing: "0.02em", backdropFilter: "blur(8px)", backgroundColor: "rgba(255,255,255,0.07)", "&:hover": { borderColor: "white", backgroundColor: "rgba(255,255,255,0.14)" } }}
              >
                + Add Suite or Villa
              </Button>
            </Box>
          )}

          {/* View Details Dialog */}
          <Dialog open={Boolean(openVilla)} onClose={handleClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { backgroundColor: "rgba(255,255,255,0.08)", backdropFilter: "blur(24px)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "16px" } }}>
            {openVilla && (
              <>
                <DialogTitle sx={{ fontFamily: modernDisplayFont, fontWeight: 600, color: "white" }}>
                  {openVilla.name}
                  <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8, color: "white" }}><Close /></IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ color: "white", borderColor: "rgba(255,255,255,0.15)" }}>
                  <img src={resolveImage(openVilla)} alt={openVilla.name} style={{ width: "100%", borderRadius: 8, marginBottom: 12 }} />
                  <Typography variant="subtitle1" gutterBottom sx={{ color: "rgba(255,255,255,0.8)" }}>{openVilla.subtitle}</Typography>
                  <Typography variant="body1" sx={{ color: "white" }}>
                    <b>Size:</b> {openVilla.size}<br />
                    <b>Max Occupancy:</b> {openVilla.maxOccupancy}<br />
                    <b>Beds:</b> {openVilla.bedConfig}
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight="600" sx={{ color: "white" }}>{openVilla.amenitiesLabel}</Typography>
                    <ul style={{ paddingLeft: "1.2em", margin: 0 }}>
                      {(openVilla.amenities || []).map((amenity, index) => (
                        <li key={index} style={{ display: "flex", alignItems: "center", marginBottom: 4, color: "white" }}>
                          <span style={{ marginRight: 6, color: "rgb(59,130,246)" }}>{AMENITY_ICONS[index] || <FaBell />}</span>
                          <Typography variant="body2" sx={{ color: "white", fontSize: "0.9rem" }}>{amenity.text}</Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} sx={{ border: "1px solid rgba(255,255,255,0.25)", color: "white", backgroundColor: "rgba(255,255,255,0.08)", borderRadius: "8px" }}>Close</Button>
                  <Button variant="contained" component="a" href="/booking" sx={{ backgroundColor: "rgba(15,23,42,0.9)", color: "white", borderRadius: "8px" }}>Book Now</Button>
                </DialogActions>
              </>
            )}
          </Dialog>

          {/* Admin Edit Dialog */}
          <Dialog open={Boolean(editVilla)} onClose={handleEditClose} maxWidth="sm" fullWidth
            PaperProps={{ sx: { backgroundColor: "#0d1b4c", border: "1px solid rgba(255,255,255,0.15)", borderRadius: "16px" } }}>
            {editVilla && (
              <>
                <DialogTitle sx={{ fontFamily: modernDisplayFont, fontWeight: 600, color: "white", display: "flex", alignItems: "center", gap: 1 }}>
                  <EditIcon sx={{ fontSize: 18 }} /> Edit — {editVilla.name}
                  <IconButton onClick={handleEditClose} sx={{ position: "absolute", right: 8, top: 8, color: "white" }}><Close /></IconButton>
                </DialogTitle>

                <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>

                  {/* Image upload section */}
                  <Box>
                    <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: 12, letterSpacing: 1, textTransform: "uppercase", mb: 1 }}>
                      Property Photo
                    </Typography>
                    <Box sx={{ position: "relative", borderRadius: "10px", overflow: "hidden", mb: 1 }}>
                      <img src={previewUrl} alt="preview" style={{ width: "100%", height: 160, objectFit: "cover", display: "block", borderRadius: 10 }} />
                      {uploadingImg && (
                        <Box sx={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.55)", display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "10px" }}>
                          <CircularProgress size={32} sx={{ color: "white" }} />
                        </Box>
                      )}
                    </Box>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      style={{ display: "none" }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<PhotoCameraIcon />}
                      onClick={() => fileInputRef.current.click()}
                      disabled={uploadingImg}
                      sx={{ color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.25)", borderRadius: "8px", fontSize: 12, "&:hover": { borderColor: "rgba(255,255,255,0.6)", backgroundColor: "rgba(255,255,255,0.05)" } }}
                    >
                      {uploadingImg ? "Uploading..." : "Change Photo"}
                    </Button>
                  </Box>

                  {/* Text fields */}
                  {[
                    { label: "Name",        field: "name",     helperText: "Full display name shown on the card — e.g. \"Sunset Palms Suite\"" },
                    { label: "Description", field: "subtitle", helperText: "Short line under the name — e.g. \"Premium Beachfront\"" },
                    { label: "Room Size (sq ft)", field: "size", helperText: "Floor area in square feet — e.g. \"1,200 sq ft\"" },
                  ].map(({ label, field, helperText }) => (
                    <TextField key={field} label={label} value={editForm[field] || ""} onChange={(e) => setEditForm((p) => ({ ...p, [field]: e.target.value }))} fullWidth size="small"
                      helperText={helperText}
                      InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                      InputProps={{ style: { color: "white" } }}
                      FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                      sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                    />
                  ))}

                  {/* Price — $ prefix locked in, digits only */}
                  <TextField label="Price" value={editForm.price || ""}
                    onChange={(e) => setEditForm((p) => ({ ...p, price: sanitizePrice(e.target.value) }))}
                    fullWidth size="small"
                    helperText="Numbers only (decimals OK, e.g. 999.99) — the $ is added automatically. Shown as $XXX/night on the card."
                    InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                    InputProps={{
                      style: { color: "white" },
                      startAdornment: <InputAdornment position="start" sx={{ "& .MuiTypography-root": { color: "rgba(255,255,255,0.8)", fontWeight: 600 } }}>$</InputAdornment>,
                      inputMode: "numeric",
                    }}
                    FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                  />

                  {/* Max Occupancy — dropdown */}
                  <FormControl fullWidth size="small"
                    sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}>
                    <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>Max Occupancy</InputLabel>
                    <Select
                      label="Max Occupancy"
                      value={editForm.maxOccupancy || ""}
                      onChange={(e) => setEditForm((p) => ({ ...p, maxOccupancy: e.target.value }))}
                      sx={{ color: "white", "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" } }}
                      MenuProps={{ PaperProps: { sx: { backgroundColor: "#0d1b4c", "& .MuiMenuItem-root": { color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" }, "&.Mui-selected": { backgroundColor: "rgba(255,255,255,0.15)" } } } } }}
                    >
                      {MAX_OCCUPANCY_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}>
                      Max number of guests this suite or villa sleeps
                    </FormHelperText>
                  </FormControl>

                  {/* Bed Config — dropdown */}
                  <FormControl fullWidth size="small"
                    sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}>
                    <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>Bed Type</InputLabel>
                    <Select
                      label="Bed Type"
                      value={editForm.bedConfig || ""}
                      onChange={(e) => setEditForm((p) => ({ ...p, bedConfig: e.target.value }))}
                      sx={{ color: "white", "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" } }}
                      MenuProps={{ PaperProps: { sx: { backgroundColor: "#0d1b4c", "& .MuiMenuItem-root": { color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" }, "&.Mui-selected": { backgroundColor: "rgba(255,255,255,0.15)" } } } } }}
                    >
                      {BED_CONFIG_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}>
                      Bed types and counts in the room
                    </FormHelperText>
                  </FormControl>

                  <TextField label="Features (one per line)" value={editForm.amenitiesText || ""} onChange={(e) => setEditForm((p) => ({ ...p, amenitiesText: e.target.value }))} fullWidth multiline rows={5}
                    helperText='Type one feature per line, shown with icons on the card — e.g. "Private infinity pool"'
                    InputLabelProps={{ style: { color: "rgba(255,255,255,0.6)" } }}
                    InputProps={{ style: { color: "white", fontSize: "0.9rem" } }}
                    FormHelperTextProps={{ style: { color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" } }}
                    sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}
                  />

                  {/* Property Type — dropdown */}
                  <FormControl fullWidth size="small" required
                    sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: "rgba(255,255,255,0.2)" }, "&:hover fieldset": { borderColor: "rgba(255,255,255,0.5)" }, "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,0.8)" } } }}>
                    <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>Property Type</InputLabel>
                    <Select
                      label="Property Type"
                      value={editForm.propertyType || ""}
                      onChange={(e) => setEditForm((p) => ({ ...p, propertyType: e.target.value }))}
                      sx={{ color: "white", "& .MuiSvgIcon-root": { color: "rgba(255,255,255,0.6)" } }}
                      MenuProps={{ PaperProps: { sx: { backgroundColor: "#0d1b4c", "& .MuiMenuItem-root": { color: "white", "&:hover": { backgroundColor: "rgba(255,255,255,0.1)" }, "&.Mui-selected": { backgroundColor: "rgba(255,255,255,0.15)" } } } } }}
                    >
                      {PROPERTY_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                    <FormHelperText sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem" }}>
                      Classifies this listing as a Suite or Villa
                    </FormHelperText>
                  </FormControl>

                  {/* Availability toggle */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 1.5, borderRadius: "8px", border: "1px solid rgba(255,255,255,0.15)", backgroundColor: "rgba(255,255,255,0.04)" }}>
                    <Box>
                      <Typography sx={{ color: "white", fontSize: 14, fontWeight: 500 }}>Availability</Typography>
                      <Typography sx={{ color: "rgba(255,255,255,0.45)", fontSize: "0.72rem", mt: 0.25 }}>
                        {editVilla.available !== false ? "Accepting bookings — visible in booking form" : "Bookings paused — hidden from booking form"}
                      </Typography>
                    </Box>
                    <Box onClick={(e) => handleToggleAvailability(e, editVilla)}
                      sx={{ cursor: "pointer", backgroundColor: editVilla.available !== false ? "rgba(22,163,74,0.9)" : "rgba(185,28,28,0.85)", color: "#fff", px: 2, py: 0.75, borderRadius: "8px", fontSize: "0.8rem", fontWeight: 700, userSelect: "none", minWidth: 104, textAlign: "center", flexShrink: 0, transition: "background 0.2s", "&:hover": { filter: "brightness(1.1)" } }}>
                      {editVilla.available !== false ? "Available" : "Unavailable"}
                    </Box>
                  </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2, justifyContent: "space-between" }}>
                  <Button onClick={handleDelete} disabled={saving}
                    sx={{ color: "#f87171", border: "1px solid rgba(248,113,113,0.4)", borderRadius: "8px", "&:hover": { backgroundColor: "rgba(248,113,113,0.1)", borderColor: "#f87171" } }}>
                    Delete Listing
                  </Button>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button onClick={handleEditClose} sx={{ color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: "8px" }}>Cancel</Button>
                    <Button variant="contained" onClick={handleEditSave} disabled={saving || uploadingImg}
                      sx={{ backgroundColor: savedMsg ? "#1a7a4a" : "white", color: savedMsg ? "white" : "#0d1b4c", fontWeight: 600, borderRadius: "8px", "&:hover": { backgroundColor: savedMsg ? "#1a7a4a" : "#f0f0f0" } }}>
                      {saving ? "Saving..." : savedMsg ? "✓ Saved!" : "Save Changes"}
                    </Button>
                  </Box>
                </DialogActions>
              </>
            )}
          </Dialog>

        </Box>

        {/* Footer */}
        <Footer sx={{ marginTop: "auto", width: "100vw", left: "50%", marginLeft: "-50vw", marginRight: "-50vw" }} />
      </Box>
    </Box>
  )
}